import styled from '@emotion/styled';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import type { AddPrefixToObjectKeys } from 'src/utilities/types/AddPrefixToObjectKeys';

import { type BoxProps, getBoxStyles } from '../box/Box';
import { themeStyles, themeVars } from '../theme/theme';
import { type ButtonSize, type ButtonVariant } from './constants';

// Questions:
// 1. Do we need set focus color for transparent buttons?
// 1. Does dark appearance has only 2 variants (secondary & ghost)

export type FrontButtonProps = PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> &
    BoxProps & {
      size?: ButtonSize;
      variant?: ButtonVariant;
      appearance?: 'onLight' | 'onDark';
      leftSection?: ReactNode;
      rightSection?: ReactNode;
      isIcon?: boolean;
    }
>;

const Component = forwardRef<HTMLButtonElement, FrontButtonProps>((props, ref) => {
  const {
    size = 'md',
    variant = 'primaryNeutral',
    appearance = 'onLight',
    leftSection,
    rightSection,
    children,
    disabled,
    isIcon,
    ...other
  } = props;

  return (
    <Root
      data-size={size}
      data-variant={variant}
      data-appearance={appearance}
      data-disabled={disabled}
      data-is-icon={isIcon}
      ref={ref}
      {...other}
    >
      <Hover data-hover />
      <Inner data-size={size}>
        {!isIcon && leftSection && <LeftSection data-section>{leftSection}</LeftSection>}
        <Label data-is-icon={isIcon}>{children}</Label>
        {!isIcon && rightSection && <RightSection data-section>{rightSection}</RightSection>}
      </Inner>
    </Root>
  );
});

const Hover = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  background: black;
  transition: all 0.3s ease;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 999px;
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 100%;

  &[data-size='xs'] {
    gap: ${themeVars.spacings.sp03};
  }

  &[data-size='sm'] {
    gap: ${themeVars.spacings.sp03};
  }

  &[data-size='md'] {
    gap: ${themeVars.spacings.sp04};
  }
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
  position: relative;

  &[data-is-icon='true'] {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const LeftSection = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RightSection = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type RootProps = BoxProps &
  Required<
    AddPrefixToObjectKeys<Pick<FrontButtonProps, 'size' | 'variant' | 'appearance'>, 'data-'>
  >;

const Root = styled('button')<RootProps>`
  ${getBoxStyles};

  overflow: hidden;
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
  border-radius: 999px;
  background-clip: padding-box;
  border-width: 1px;
  border-style: solid;
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${themeVars.colors.white},
      0 0 0 4px ${themeVars.colors.info600};
  }

  &[data-disabled='true'] {
    cursor: not-allowed;
  }

  &:not([data-disabled='true']):active {
    transform: translateY(3px);
  }

  &[data-size='xs'] {
    ${themeStyles.text.button.xs};
    min-height: 24px;
    max-height: 24px;
    padding: 0 ${themeVars.spacings.sp04};

    &[data-is-icon='true'] {
      width: 24px;
      flex-shrink: 0;
      padding: 0;
    }

    [data-section] {
      font-size: 16px;
    }
  }

  &[data-size='sm'] {
    ${themeStyles.text.button.sm};
    min-height: 32px;
    max-height: 32px;
    padding: 0 ${themeVars.spacings.sp05};

    &[data-is-icon='true'] {
      flex-shrink: 0;
      width: 32px;
      padding: 0;
    }

    [data-section] {
      font-size: 20px;
    }
  }

  &[data-size='md'] {
    ${themeStyles.text.button.md};
    min-height: 40px;
    max-height: 40px;
    padding: 0 ${themeVars.spacings.sp06};

    &[data-is-icon='true'] {
      flex-shrink: 0;
      width: 40px;
      padding: 0;
    }

    [data-section] {
      font-size: 20px;
    }
  }

  &:not([data-disabled='true']):hover {
    [data-hover] {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }

  &[data-variant='primaryBrand'] {
    background: ${themeVars.colors.neonFrontRow200};
    border-color: ${themeVars.colors.neonFrontRow200};
    color: ${themeVars.colors.coldGrey900};
    stroke: ${themeVars.colors.coldGrey900};

    [data-hover] {
      background: ${themeVars.colors.neonFrontRow300};
    }

    &:hover {
      border-color: ${themeVars.colors.neonFrontRow300};
    }

    &:active {
      border-color: ${themeVars.colors.neonFrontRow400};

      [data-hover] {
        background: ${themeVars.colors.neonFrontRow400};
      }
    }

    &[data-disabled='true'] {
      border: none;
      background: ${themeVars.colors.disabledFill};
      color: ${themeVars.colors.disabledContent};
      stroke: ${themeVars.colors.disabledContent};
    }
  }

  &[data-variant='primaryNeutral'] {
    background: ${themeVars.colors.titan900};
    border-color: ${themeVars.colors.titan900};
    color: ${themeVars.colors.titan25};
    stroke: ${themeVars.colors.titan25};

    [data-hover] {
      background: ${themeVars.colors.titan800};
    }

    &:hover {
      border-color: ${themeVars.colors.titan800};
    }

    &:active {
      border-color: ${themeVars.colors.titan700};

      [data-hover] {
        background: ${themeVars.colors.titan700};
      }
    }

    &[data-disabled='true'] {
      border: none;
      background: ${themeVars.colors.disabledFill};
      color: ${themeVars.colors.disabledContent};
      stroke: ${themeVars.colors.disabledContent};
    }
  }

  &[data-variant='secondary'] {
    background: transparent;
    border-color: ${themeVars.colors.titan900};
    color: ${themeVars.colors.titan900};
    stroke: ${themeVars.colors.titan900};

    [data-hover] {
      background: ${themeVars.colors.titan800};
    }

    &:hover {
      border-color: ${themeVars.colors.titan800};
      color: ${themeVars.colors.titan25};
      stroke: ${themeVars.colors.titan25};
    }

    &:active {
      border-color: ${themeVars.colors.titan700};
      color: ${themeVars.colors.titan25};
      stroke: ${themeVars.colors.titan25};

      [data-hover] {
        background: ${themeVars.colors.titan700};
      }
    }

    &[data-disabled='true'] {
      background: transparent;
      border-color: ${themeVars.colors.disabledFill};
      color: ${themeVars.colors.disabledFill};
      stroke: ${themeVars.colors.disabledFill};
    }

    &[data-appearance='onDark'] {
      background: transparent;
      border-color: ${themeVars.colors.neonFrontRow200};
      color: ${themeVars.colors.neonFrontRow200};
      stroke: ${themeVars.colors.neonFrontRow200};

      [data-hover] {
        background: ${themeVars.colors.neonFrontRow300};
      }

      &:hover {
        color: ${themeVars.colors.coldGrey900};
        stroke: ${themeVars.colors.coldGrey900};
      }

      &:active {
        border-color: ${themeVars.colors.neonFrontRow400};
        color: ${themeVars.colors.coldGrey900};
        stroke: ${themeVars.colors.coldGrey900};

        [data-hover] {
          background: ${themeVars.colors.neonFrontRow400};
        }
      }

      &[data-disabled='true'] {
        background: transparent;
        border-color: ${themeVars.colors.disabledFill};
        color: ${themeVars.colors.disabledContent};
        stroke: ${themeVars.colors.disabledContent};
      }
    }
  }

  &[data-variant='tertiary'] {
    background: ${themeVars.colors.titan200};
    border-color: ${themeVars.colors.titan200};
    color: ${themeVars.colors.coldGrey800};
    stroke: ${themeVars.colors.coldGrey800};

    [data-hover] {
      background: ${themeVars.colors.titan300};
    }

    &:hover {
      border-color: ${themeVars.colors.titan300};
    }

    &:active {
      border-color: ${themeVars.colors.titan400};

      [data-hover] {
        background: ${themeVars.colors.titan400};
      }
    }

    &[data-disabled='true'] {
      border: none;
      background: ${themeVars.colors.disabledFill};
      color: ${themeVars.colors.disabledContent};
      stroke: ${themeVars.colors.disabledContent};
    }
  }

  &[data-variant='quaternary'] {
    background: transparent;
    border-color: ${themeVars.colors.titan200};
    color: ${themeVars.colors.coldGrey600};
    stroke: ${themeVars.colors.coldGrey600};

    [data-hover] {
      background: ${themeVars.colors.titan300};
    }

    &:hover {
      border-color: ${themeVars.colors.titan300};
      color: ${themeVars.colors.coldGrey900};
      stroke: ${themeVars.colors.coldGrey900};
    }

    &:active {
      border-color: ${themeVars.colors.titan400};
      color: ${themeVars.colors.coldGrey900};
      stroke: ${themeVars.colors.coldGrey900};

      [data-hover] {
        background: ${themeVars.colors.titan400};
      }
    }

    &[data-disabled='true'] {
      background: transparent;
      border-color: ${themeVars.colors.disabledFill};
      color: ${themeVars.colors.disabledFill};
      stroke: ${themeVars.colors.disabledFill};
    }
  }

  &[data-variant='ghost'] {
    background: transparent;
    border: none;
    color: ${themeVars.colors.coldGrey800};
    stroke: ${themeVars.colors.coldGrey800};

    [data-hover] {
      background: ${themeVars.colors.titan300};
    }

    &:hover {
      border-color: ${themeVars.colors.titan300};
    }

    &:active {
      [data-hover] {
        background: ${themeVars.colors.titan400};
      }
    }

    &[data-disabled='true'] {
      background: transparent;
      color: ${themeVars.colors.disabledContent};
      stroke: ${themeVars.colors.disabledContent};
    }

    &[data-appearance='onDark'] {
      background: transparent;
      color: ${themeVars.colors.neonFrontRow200};
      stroke: ${themeVars.colors.neonFrontRow200};

      [data-hover] {
        background: ${themeVars.colors.neonFrontRow300};
      }

      &:hover {
        color: ${themeVars.colors.coldGrey900};
        stroke: ${themeVars.colors.coldGrey900};
      }

      &:active {
        color: ${themeVars.colors.coldGrey900};
        stroke: ${themeVars.colors.coldGrey900};
        [data-hover] {
          background: ${themeVars.colors.neonFrontRow400};
        }
      }

      &[data-disabled='true'] {
        background: transparent;
        border-color: transparent;
        color: ${themeVars.colors.disabledContent};
        stroke: ${themeVars.colors.disabledContent};
      }
    }
  }
`;

export const FrontButton = Object.assign(Component, {
  Root,
  Label,
  LeftSection,
  RightSection,
  Inner,
  Hover,
});
